// Wraps the two session-download surfaces in a single modal behind General /
// Tracking tabs, so soccer sessions reach tracking from the same Export action
// (not a separate button). The app passes its already-built General
// (DownloadForm) node plus the tracking inputs; this owns the tab shell, its
// labels, and the tracking fetch.
//
// The tracking fetch lives here (not inside the Tracking pane) so it prefetches
// as soon as the modal opens and the manifest is ready by the time the user
// switches tabs. Both panes are always rendered, overlaid in one grid cell, so
// the modal always sizes to the taller tab and never resizes on switch; the
// inactive pane is hidden with `opacity-0` (keeps its footprint for sizing) plus
// `pointer-events-none` and `aria-hidden` so it takes no clicks and leaves the
// accessibility tree. Opacity (not `visibility`) avoids a switch-time flash:
// `visibility` inherits, and the button's `transition-all` would delay the
// inherited change, briefly overlapping both panes' actions. The tab bar is
// controlled so hiding never depends on the tab primitive's own mount behaviour.
import { cn, Tabs, TabsList, TabsTrigger } from '@frontend/ui'
import { useState, type ReactNode } from 'react'
import { TrackingDownloadPanel } from './tracking-download-panel'
import { useTrackingDownload } from './use-tracking-download'

interface SessionDownloadTabsProps {
  generalTab: ReactNode
  tracking: {
    baseUrl: string | null
    sessionId: string
    sessionDate: string
    orgId?: string
    onClose: () => void
  }
}

export function SessionDownloadTabs({
  generalTab,
  tracking
}: SessionDownloadTabsProps) {
  const [value, setValue] = useState('general')
  const { state, onDownload, retry } = useTrackingDownload({
    baseUrl: tracking.baseUrl,
    sessionId: tracking.sessionId,
    sessionDate: tracking.sessionDate,
    orgId: tracking.orgId
  })

  return (
    <Tabs value={value} onValueChange={setValue}>
      <TabsList className='mb-6'>
        <TabsTrigger value='general'>General</TabsTrigger>
        <TabsTrigger value='tracking'>Tracking</TabsTrigger>
      </TabsList>
      <div className='grid'>
        <div
          className={cn(
            'col-start-1 row-start-1',
            value !== 'general' && 'pointer-events-none opacity-0'
          )}
          aria-hidden={value !== 'general' || undefined}
        >
          {generalTab}
        </div>
        <div
          className={cn(
            'col-start-1 row-start-1',
            value !== 'tracking' && 'pointer-events-none opacity-0'
          )}
          aria-hidden={value !== 'tracking' || undefined}
        >
          <TrackingDownloadPanel
            state={state}
            onDownload={onDownload}
            onRetry={retry}
            onClose={tracking.onClose}
          />
        </div>
      </div>
    </Tabs>
  )
}
